import type { ClassificationResult } from '@classification-testing/domain';
import { getLanguageDisplayInfo, getLanguageName } from '@classification-testing/utils/languageHelpers';
import { Text, Badge, BadgeSize, Icon, Warning, IconSize } from '@soteria/design-system-ve';

import * as styles from './ClassificationResultItem.css';

// Helper function to get content type display name
function getContentTypeDisplayName(contentType: string): string {
  const typeMap: Record<string, string> = {
    TEXT: 'Text',
    IMAGE: 'Image',
    AUDIO: 'Audio',
    MIXED: 'Mixed',
  };

  return typeMap[contentType] || 'Content';
}

// Helper to render label badges
function LabelBadges({ result }: { result: ClassificationResult }) {
  if (!result.labels || result.labels.length === 0) {
    return null;
  }

  return (
    <div className={styles.metadataItem}>
      <Text className={styles.metadataLabel}>Labels:</Text>
      <div className={styles.labelBadges}>
        {result.labels.slice(0, 2).map(label => (
          <Badge
            key={label.label_id || label.label_name}
            size={BadgeSize.Small}
            text={label.label_short_name || label.label_name}
            title={`${label.label_name} (Severity: ${label.severity}${label.confidence ? `, Confidence: ${(label.confidence * 100).toFixed(4)}%` : ''})`}
          />
        ))}
        {result.labels.length > 2 && <Text className={styles.moreLabelsText}>+{result.labels.length - 2} more</Text>}
      </div>
    </div>
  );
}

// Helper to check if policies exist
function hasPolicyData(result: ClassificationResult): boolean {
  return (result.policies?.length ?? 0) > 0 || (result.nonTriggeredPolicies?.length ?? 0) > 0;
}

// Helper to render policy badges
function PolicyBadges({ result }: { result: ClassificationResult }) {
  if (!hasPolicyData(result)) {
    return null;
  }

  const hasTriggeredPolicies = (result.policies?.length ?? 0) > 0;

  return (
    <div className={styles.metadataItem}>
      <Text className={styles.metadataLabel}>Triggered Policies:</Text>
      {!hasTriggeredPolicies ? (
        <Badge size={BadgeSize.Small} text="None" />
      ) : (
        <div className={styles.labelBadges}>
          {result.policies?.map(policy => <Badge key={policy.id} size={BadgeSize.Small} text={policy.name} title={policy.description} />)}
        </div>
      )}
    </div>
  );
}

interface MetadataSectionProps {
  result: ClassificationResult;
}

/**
 * Metadata badges section component
 */
export function MetadataSection({ result }: MetadataSectionProps) {
  // Get language display information
  const languageInfo = getLanguageDisplayInfo(result.languageDetected, result.languageClassified);
  const showLanguage = result.languageDetected || result.languageClassified;

  return (
    <div className={styles.metadataLeftSection}>
      <div className={styles.metadataItem}>
        <Text className={styles.metadataLabel}>Topic:</Text>
        <Badge size={BadgeSize.Small} text="Default" />
      </div>

      {showLanguage && (
        <div className={styles.metadataItem}>
          <Text className={styles.metadataLabel}>Language:</Text>
          <Badge size={BadgeSize.Small} text={getLanguageName(result.languageClassified || result.languageDetected)} />
          {languageInfo.showTranslationIndicator && <Icon icon={Warning} iconSize={IconSize.XSmall} title={languageInfo.tooltipText} />}
        </div>
      )}

      <div className={styles.metadataItem}>
        <Text className={styles.metadataLabel}>Content Type:</Text>
        <Badge size={BadgeSize.Small} text={getContentTypeDisplayName(result.contentType).toUpperCase()} />
      </div>

      <LabelBadges result={result} />
      <PolicyBadges result={result} />
    </div>
  );
}
